import React, { useState, useEffect } from 'react';
import PropTypes, { any } from 'prop-types';

import DivisionSelection from './DivisionSelection';

import { getRefundDataForEvent, renderDivisionPrice } from '../../utils/events';
import { utrTypeIds, paymentStatusTypeIds, regStepIds, sportTypeIds, ANNUAL_POWER_ONLY_LABEL } from '../../utils/eventConstants';
import { getApiAxiosInstance } from '../../utils/network';

// supports V2 typeIds
const CancelDivision = ({
    event,
    user,
    submitSelfWithdraw,
    submitting,
    clubVerificationFee,
    sidebarFlow,
    handleSwitchNext,
    canceledDivisionIds = [],
    divisionFormatTypeFilter,
    setDivisionFormatTypeFilter,
    registrations,
}) => {
    const [selectedDivisions, setSelectedDivisions] = useState([]);
    const [eligibleDivisions, setEligibleDivisions] = useState([]);
    const [ineligibleDivisions, setIneligibleDivisions] = useState([]);
    const [feesForfeited, setFeesForfeited] = useState(0);
    const [eventFeesRefund, setEventFeesRefund] = useState(0);
    const [verifiedFeesRefunded, setVerifiedFeesRefunded] = useState(0);
    const [couponRefund, setCouponRefund] = useState(0);
    const [serviceFeeRefund, setServiceFeeRefund] = useState(0);
    const [playerEventEntryFeeRefund, setPlayerEventEntryFeeRefund] = useState(0);
    const [cancellationFee, setCancellationFee] = useState(0);
    const [totalRefund, setTotalRefund] = useState(0);
    const [isLoadingCalculation, setIsLoadingCalculation] = useState();
    const [isCalculationLoaded, setIsCalculationLoaded] = useState();

    const isSanctioned = event.utrType?.value === 'sanctioned' || event.utrTypeId === utrTypeIds.SANCTIONED;
    const isSwitchFlow = sidebarFlow === regStepIds.DIVISIONS_SWITCH;
    const apiInstance = getApiAxiosInstance();
    const eventId = event?.id;
    const memberId = user?.id;
    const currencySymbol = event?.currency?.symbol || '$';

    const loadCalculation = async (divisionsToCalculateRefunds) => {
        setIsLoadingCalculation(true);
        const promoCodeRefundRequest = {
            divisionIds: divisionsToCalculateRefunds.map(div => div.id),
            memberId,
        };
        const isSelfWithdraw = !isSwitchFlow;
        const { totalDiscountsPlayerReceived = 0, serviceFeeRefundTotal = 0, playerEventEntryFeeRefundTotal = 0, verificationFeeRefundTotal = 0, feesForfeitedTotal = 0, divisionFeeRefundedTotal = 0, totalRefundAmount = 0, cancellationFeeTotal = 0 } = await getRefundDataForEvent(apiInstance, eventId, promoCodeRefundRequest, isSelfWithdraw);
        setCouponRefund(totalDiscountsPlayerReceived);
        setServiceFeeRefund(serviceFeeRefundTotal);
        setPlayerEventEntryFeeRefund(playerEventEntryFeeRefundTotal);
        setEventFeesRefund(divisionFeeRefundedTotal);
        setFeesForfeited(feesForfeitedTotal);
        setVerifiedFeesRefunded(verificationFeeRefundTotal);
        setTotalRefund(totalRefundAmount);
        setCancellationFee(cancellationFeeTotal);
        setIsLoadingCalculation(false);
        setIsCalculationLoaded(true);
    };

    useEffect(() => {
        const divisionIds = event.currentMember.registeredDivisionIds;
        const allRegisteredDivisions = event.eventDivisions.filter(div => divisionIds.includes(div.id));
        // user specific division props from the /divisions/registrations endpoint
        const registrationsDivs = registrations?.data?.registeredDivisions || [];

        const {
            eligible,
            inEligible,
        } = allRegisteredDivisions.reduce((acc, curr) => {
            const divObjFromRegistrationsApi = registrationsDivs.find(div => div.divisionId === curr.id);
            const { selfWithdrawAllowed, autoRefundEnabled, total, availableDivisionsForSwitching } = divObjFromRegistrationsApi || {};
            if ((!isSwitchFlow && selfWithdrawAllowed) || (isSwitchFlow && availableDivisionsForSwitching?.length)) {
                acc.eligible.push({
                    ...curr,
                    selfWithdrawAllowed,
                    autoRefundEnabled,
                    isRefundable: selfWithdrawAllowed && autoRefundEnabled && total > 0,
                });
            } else if (!curr.isEventEntryFeeDivision) {
                acc.inEligible.push(curr);
            }
            return acc;
        }, {
            eligible: [],
            inEligible: [],
        });

        setEligibleDivisions(eligible);
        if (canceledDivisionIds.length) {
            const divisionsToCalculateRefunds = eligible.filter(div => canceledDivisionIds.includes(div.id));
            setSelectedDivisions(divisionsToCalculateRefunds);
            if (!isCalculationLoaded) {
                loadCalculation(divisionsToCalculateRefunds);
            }
        }
        setIneligibleDivisions(inEligible);
    }, [registrations]);

    useEffect(() => {
        if (isSwitchFlow && selectedDivisions.length && divisionFormatTypeFilter !== selectedDivisions[0].divisionFormatTypeId) {
            setDivisionFormatTypeFilter(selectedDivisions[0].divisionFormatTypeId);
        } else if (isSwitchFlow && !selectedDivisions.length) {
            setDivisionFormatTypeFilter();
        }
    }, [selectedDivisions]);

    const handleSelectDivision = async (division) => {
        let divisionsToCalculateRefunds = [];
        if (selectedDivisions.some(({ id }) => id === division.id)) {
            setSelectedDivisions(selectedDivisions.filter(({ id }) => id !== division.id));
            divisionsToCalculateRefunds = selectedDivisions.filter(({ id }) => id !== division.id);
        } else {
            setSelectedDivisions([...selectedDivisions, division]);
            divisionsToCalculateRefunds = [...selectedDivisions, division];
        }

        if (divisionsToCalculateRefunds.length > 0) {
            loadCalculation(divisionsToCalculateRefunds);
        } else {
            // don't bother calling the api if there are no coupons to calculate
            setCouponRefund(0);
        }
        if (isSwitchFlow && divisionFormatTypeFilter !== division.divisionFormatTypeId) {
            setDivisionFormatTypeFilter(division.divisionFormatTypeId);
        }
    };

    const allSelected = () => {
        return eligibleDivisions.every(({ id }) => (
            selectedDivisions.some(selected => selected.id === id)
        ));
    };

    const handleSelectAll = async () => {
        if (allSelected()) {
            setSelectedDivisions([]);
            // reset the promo code line to be zero
            setCouponRefund(0);
        } else {
            setSelectedDivisions([...eligibleDivisions]);
            const promoCodeRefundRequest = {
                divisionIds: eligibleDivisions.map(div => div.id),
                memberId,
            };
            const isSelfWithdraw = !isSwitchFlow;
            const { totalDiscountsPlayerReceived = 0, serviceFeeRefundTotal = 0, playerEventEntryFeeRefundTotal = 0, verificationFeeRefundTotal = 0, feesForfeitedTotal = 0, divisionFeeRefundedTotal = 0, totalRefundAmount = 0, cancellationFeeTotal = 0 } = await getRefundDataForEvent(apiInstance, eventId, promoCodeRefundRequest, isSelfWithdraw);
            setCouponRefund(totalDiscountsPlayerReceived);
            setServiceFeeRefund(serviceFeeRefundTotal);
            setPlayerEventEntryFeeRefund(playerEventEntryFeeRefundTotal);
            setEventFeesRefund(divisionFeeRefundedTotal);
            setFeesForfeited(feesForfeitedTotal);
            setVerifiedFeesRefunded(verificationFeeRefundTotal);
            setTotalRefund(totalRefundAmount);
            setCancellationFee(cancellationFeeTotal);
            setIsLoadingCalculation(false);
            setIsCalculationLoaded(true);
        }
    };

    const handleRegistrationWithdraw = () => {
        const withdrawPayload = {
            userId: user.id,
            divisionIds: selectedDivisions.map(({ id }) => id),
            eventId: event.id,
        };
        submitSelfWithdraw(withdrawPayload);
    };

    const handleNextClick = () => {
        if (isSwitchFlow) {
            handleSwitchNext({
                divisionIds: selectedDivisions.map(({ id }) => id),
                divisionFeeRefund: (eventFeesRefund + verifiedFeesRefunded),
                totalRefund,
            });
        } else {
            handleRegistrationWithdraw();
        }
    };

    const renderPrice = (division) => {
        return renderDivisionPrice(
            division,
            event.allowOffPlatformPayments,
            isSanctioned,
            event.workflowVersion,
            user,
            clubVerificationFee,
        );
    };

    const renderPriceDescription = (division, isEligibleForRefund) => {
        const userRegistration = event.currentMember?.registrationDetails?.find(reg => reg.divisionId === division.id) || event.currentMember?.divisions?.find(reg => reg.divisionId === division.id);

        if (userRegistration?.paymentStatusType?.value === 'refunded' || userRegistration?.paymentStatusTypeId === paymentStatusTypeIds.REFUNDED) {
            return 'Refunded';
        }
        if (isEligibleForRefund === true) {
            return 'Refundable';
        }
        return 'Non-refundable';
    };

    return (
        <>
            {!isSwitchFlow && (
                <>
                    <div className="text-highlight2 text-dark mb16">
                        Select division(s) to withdraw from
                    </div>
                    <div onClick={handleSelectAll} className="d-flex justify-between align-items-baseline">
                        <input type="checkbox" checked={allSelected()} />
                        <p className="ml-1 text-small">Select All</p>
                    </div>
                </>
            )}
            {isSwitchFlow && (
                <div className="mb16">
                    <div className="text-highlight2 text-dark">Select division to switch</div>
                    <div className="text-dark text-small">Only divisions eligible to change are shown, and you can only switch one division at a time.</div>
                </div>
            )}
            {eligibleDivisions.map((division, i) => {
                const selected = selectedDivisions.some(({ id }) => division.id === id);
                return (
                    <DivisionSelection
                        key={i}
                        selected={selected}
                        division={division}
                        divisionPriceDescription={renderPriceDescription(division, division.isRefundable)}
                        divisionPrice={() => (
                            <>{(division.isRefundable || division.entryFee === null) ? renderPrice(division) : <del>{renderPrice(division)}</del>}</>
                        )}
                        subTitle={division.annualPoweredOnly && ANNUAL_POWER_ONLY_LABEL}
                        description={division.description}
                        handleClick={() => handleSelectDivision(division)}
                        disabled={(divisionFormatTypeFilter && divisionFormatTypeFilter !== division.divisionFormatTypeId) || (isSwitchFlow && selectedDivisions.length && !selected)}
                        sportTypeId={event?.sportTypeId}
                    />
                );
            })}
            {!isSwitchFlow && ineligibleDivisions.length > 0 && <div className="text-small text-left mb24">The following divisions cannot be cancelled directly. Please contact the organizer.</div>}
            {!isSwitchFlow && ineligibleDivisions.map((division, i) => {
                return (
                    <DivisionSelection
                        key={i}
                        division={division}
                        divisionPrice={() => (
                            <>{renderPrice(division)}</>
                        )}
                        subTitle={division.annualPoweredOnly && ANNUAL_POWER_ONLY_LABEL}
                        description={division.description}
                        disabled
                    />
                );
            })}
            {!isSwitchFlow && (
                <div className="mt-5">
                    {!!feesForfeited && (
                        <div className="d-flex justify-content-between">
                            <p>Fees forfeited</p>
                            <p>{currencySymbol}{feesForfeited}</p>
                        </div>
                    )}
                    <div className="d-flex justify-content-between">
                        <p>{event?.sportTypeId === sportTypeIds.PICKLEBALL ? `Transaction` : `Verified`} fees refunded</p>
                        <p>{currencySymbol}{verifiedFeesRefunded?.toFixed(2)}</p>
                    </div>
                    <div className="d-flex justify-content-between">
                        <p>Division fees refunded</p>
                        <p>{currencySymbol}{eventFeesRefund?.toFixed(2)}</p>
                    </div>
                    {couponRefund > 0 && (
                        <div className="d-flex justify-content-between">
                            <p>Coupon refund</p>
                            <p>-{currencySymbol}{couponRefund}</p>
                        </div>
                    )}
                    {serviceFeeRefund > 0 && (
                        <div className="d-flex justify-content-between">
                            <p>Service fee refunded</p>
                            <p>{currencySymbol}{serviceFeeRefund.toFixed(2)}</p>
                        </div>
                    )}
                    {playerEventEntryFeeRefund > 0 && (
                        <div className="d-flex justify-content-between">
                            <p>Entry fee refunded</p>
                            <p>{currencySymbol}{playerEventEntryFeeRefund.toFixed(2)}</p>
                        </div>
                    )}
                    {cancellationFee > 0 && (
                        <div className="d-flex justify-content-between">
                            <p>Withdrawal fee</p>
                            <p>{currencySymbol}{cancellationFee.toFixed(2)}</p>
                        </div>
                    )}
                    <div className="d-flex justify-content-between text-dark">
                        <p>Total refund</p>
                        <p>{currencySymbol}{totalRefund.toFixed(2)}</p>
                    </div>
                </div>
            )}
            {isSwitchFlow && (
                <div className="fs14 d-flex text-dark">
                    If the new division cost the same, no additional transactions are required. If the cost differs, you&apos;ll be refunded and need to pay for the new division.
                </div>
            )}
            <button
                onClick={handleNextClick}
                disabled={selectedDivisions.length === 0 || submitting || isLoadingCalculation}
                type="button"
                className={`btn btn-primary btn-xl btn-block text-uppercase mt-3 ${submitting ? 'text-dark' : ''}`}
            >
                {(submitting || isLoadingCalculation) && (<i className="fas fa-circle-notch fa-spin" />)}{submitting ? 'Processing' : isSwitchFlow ? 'Next' : 'Submit'}
            </button>
        </>
    );
};

CancelDivision.propTypes = {
    user: PropTypes.objectOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    clubVerificationFee: PropTypes.number,
    submitting: PropTypes.bool,
    submitSelfWithdraw: PropTypes.func,
    sidebarFlow: PropTypes.string,
    handleSwitchNext: PropTypes.func,
    divisionFormatTypeFilter: PropTypes.number,
    setDivisionFormatTypeFilter: PropTypes.func.isRequired,
};

CancelDivision.defaultProps = {
    clubVerificationFee: 0,
    submitting: false,
    submitSelfWithdraw: () => {},
    sidebarFlow: '',
    handleSwitchNext: () => {},
    divisionFormatTypeFilter: null,
};

export default CancelDivision;
